import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ListService } from '@abp/ng.core';
import { ConfirmationService } from '@abp/ng.theme.shared';
import { Component, DestroyRef, inject } from '@angular/core';
import {
  ControlTypes,
  FieldMapperHelper,
  GenericForm,
  GenericFormField,
  InputTypes,
  LocationTypes,
  ZnSelectQueryParams,
  ZnHierarchicalViewComponent,
} from '@zendra/shared';
import { StandService } from 'cbd-proxies';
import { WoodCertificationDetailService } from 'geographic/proxy';
import { WoodCertificationDetailProjectionDto } from 'projects/orchestrator/src/lib/proxy/upm/zendra/cms/projection/wood-certification-details/models';
import { catchError, distinctUntilChanged, map, of, startWith, Subscription, switchMap } from 'rxjs';
import { CERTIFIEDSTANDS_CONFIG, CERTIFIEDSTANDS_FIELDS } from './certified-stands.config';
import { CertifiedLocationBaseComponent } from '../certified-location-base.component';

@Component({
  selector: 'lib-certified-stands',
  imports: [
      GenericForm,
      ZnHierarchicalViewComponent,
    ],
  providers: [ListService, WoodCertificationDetailService, ConfirmationService],
  templateUrl: './certified-stands.html'
})
export class CertifiedStands extends CertifiedLocationBaseComponent {
  private readonly destroyRef = inject(DestroyRef);
  private readonly standService = inject(StandService);
  private establishmentSyncSubscription?: Subscription;

  config = CERTIFIEDSTANDS_CONFIG;
  fields = CERTIFIEDSTANDS_FIELDS;

  override create(): void {
    if (this.parentId !== undefined && this.parentFilterField) {
      this.selectedEntity = {
        [this.parentFilterField]: this.parentId,
      } as WoodCertificationDetailProjectionDto;
    } else {
      this.selectedEntity = {} as WoodCertificationDetailProjectionDto;
    }

    this.openForm(this.selectedEntity, 'create');
  }

  override edit(row: WoodCertificationDetailProjectionDto): void {
    this.selectedEntity = { ...row };
    this.openForm(row, 'edit');
  }

  protected getWoodCertificationType(): LocationTypes {
    return LocationTypes.Stand;
  }

  protected override prepareCreateDto(data: any) {
    return {
      woodCertificationId: data.woodCertificationId,
      locationId: data.locationId,
      lastCheckDate: null,
      addedDate: data.addedDate ?? null,
    };
  }

  protected getEntityName(): string {
    return 'CertifiedStands';
  }

  private openForm(
    row: Partial<WoodCertificationDetailProjectionDto>,
    mode: 'create' | 'edit',
  ): void {
    const fields = this.buildFormFields(row);
    this.genericForm.open({
      title: `${this.localizationResource.LanguageService}::${
        mode === 'create' ? 'TableNewRegistry' : 'EditRecord'
      }`,
      fields,
      entityLocaleKey: this.config.localizationKey,
      mode,
    });

    this.setupEstablishmentFieldSync(row.propertyName ?? null);
  }

  private buildFormFields(
    row: Partial<WoodCertificationDetailProjectionDto>,
  ): GenericFormField[] {
    const fields = FieldMapperHelper.mapFieldsForForm(
      this.fields,
      row,
      this.injector,
    ).filter(field => field.key !== 'propertyId');
    const locationField = fields.find(field => field.key === 'locationId');

    if (locationField) {
      locationField.optionsLoader = params => this.loadStandOptions(params);
    }

    const establishmentField = this.createEstablishmentNameField(
      row.propertyName ?? null,
    );
    const locationIndex = fields.findIndex(field => field.key === 'locationId');

    if (locationIndex === -1) {
      return [...fields, establishmentField];
    }

    return [
      ...fields.slice(0, locationIndex + 1),
      establishmentField,
      ...fields.slice(locationIndex + 1),
    ];
  }

  private createEstablishmentNameField(
    establishmentName: string | null,
  ): GenericFormField {
    return new GenericFormField({
      key: 'propertyName',
      label: 'Geographic::Entity.Property',
      controlType: ControlTypes.Textbox,
      type: InputTypes.Text,
      value: establishmentName ?? '',
    });
  }

  private setupEstablishmentFieldSync(
    initialPropertyName: string | null,
  ): void {
    this.establishmentSyncSubscription?.unsubscribe();

    const form = this.genericForm.form();
    const locationControl = form.get('locationId');
    const establishmentControl = form.get('propertyName');

    if (!locationControl || !establishmentControl) {
      return;
    }

    establishmentControl.disable({ emitEvent: false });
    establishmentControl.setValue(initialPropertyName ?? '', {
      emitEvent: false,
    });

    this.establishmentSyncSubscription = locationControl.valueChanges
      .pipe(
        startWith(locationControl.value),
        distinctUntilChanged(),
        switchMap(locationId =>
          this.resolveEstablishmentName(locationId, initialPropertyName),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(establishmentName => {
        establishmentControl.setValue(establishmentName, {
          emitEvent: false,
        });
      });
  }

  private resolveEstablishmentName(
    locationId: unknown,
    initialPropertyName: string | null,
  ) {
    const parsedLocationId = this.toNullableNumber(locationId);

    if (!parsedLocationId) {
      return of('');
    }

    if (
      this.toNullableNumber(this.selectedEntity?.locationId) === parsedLocationId &&
      initialPropertyName
    ) {
      return of(initialPropertyName);
    }

    return this.standService
      .getList(
        {
          locationId: [parsedLocationId],
          maxResultCount: 1,
        },
        { skipHandleError: true },
      )
      .pipe(
        map(response => response.items?.[0]?.property?.name ?? ''),
        catchError(() => of('')),
      );
  }

  private loadStandOptions(
    params?: ZnSelectQueryParams,
  ) {
    const locationIds = (params?.ids ?? [])
      .map(id => this.toNullableNumber(id))
      .filter((id): id is number => id !== null);

    return this.standService
      .getList(
        {
          locationId: locationIds.length ? locationIds : undefined,
          name: params?.search?.trim() || undefined,
          sorting: 'name',
          skipCount: params?.skipCount ?? 0,
          maxResultCount:
            params?.maxResultCount ?? (locationIds.length || 10),
        },
        { skipHandleError: true },
      )
      .pipe(
        map(response => ({
          items: (response.items ?? []).map(stand => ({
            key: String(stand.locationId ?? ''),
            value: stand.name ?? '',
          })),
          totalCount: response.totalCount ?? 0,
        })),
        catchError(() => of({ items: [], totalCount: 0 })),
      );
  }

  private toNullableNumber(value: unknown): number | null {
    if (typeof value === 'number') {
      return Number.isFinite(value) ? value : null;
    }

    if (typeof value !== 'string') {
      return null;
    }

    const trimmedValue = value.trim();
    if (!trimmedValue) {
      return null;
    }

    const parsedValue = Number(trimmedValue);
    return Number.isFinite(parsedValue) ? parsedValue : null;
  }
}
